import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { API_BASE_URL } from "../config";
import { toast } from "react-toastify";
import {
  FiSearch,
  FiMoreVertical,
  FiTrash2,
  FiEdit,
  FiMail,
  FiPackage,
  FiKey,
  FiChevronDown,
  FiList,
  FiVideo,
  FiUsers,
  FiChevronLeft,
  FiChevronRight,
  FiShoppingBag,
  FiRefreshCw,
} from "react-icons/fi";
import { FaTelegram } from "react-icons/fa";
import ExportButton from "./ExportButton";
import {
  Button,
  Card,
  Badge,
  PageHeader,
  DataTable,
  TableSkeleton,
  EmptyState,
  Modal,
  Tooltip,
  useConfirm,
  usePrompt,
  formatDate,
  formatDateTime,
} from "./ui";

/* ------------------------------------------------------------------ */
/* Row action menu (main dropdown + nested "Lisensi" submenu)          */
/* ------------------------------------------------------------------ */
function StoreRowActions({
  toko,
  durasiOptions,
  onGenerate,
  onKirimTelegram,
  onDetail,
  onKirimZip,
  onKirimTutorial,
  onEdit,
  onEditUserId,
  onEditJenis,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [lisensiOpen, setLisensiOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const MENU_WIDTH = 208;
  const MENU_HEIGHT = 380;

  const closeAll = () => {
    setOpen(false);
    setLisensiOpen(false);
    setGenerateOpen(false);
  };

  const openMenu = () => {
    const r = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const top = spaceBelow < MENU_HEIGHT ? Math.max(8, r.top - MENU_HEIGHT - 8) : r.bottom + 8;
    setCoords({ top, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
    setLisensiOpen(false);
    setGenerateOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      closeAll();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeAll();
    };
    const onScroll = () => closeAll();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const run = (fn) => () => {
    closeAll();
    fn(toko);
  };

  const itemCls =
    "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800";

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        onClick={() => (open ? closeAll() : openMenu())}
        className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        aria-label="Aksi toko"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <FiMoreVertical className="h-5 w-5" />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          style={{ top: coords.top, right: coords.right, width: MENU_WIDTH }}
          className="fixed z-50 origin-top-right overflow-hidden rounded-xl border border-slate-200/80 bg-white p-1.5 shadow-pop animate-fade-in-up dark:border-slate-800 dark:bg-slate-900"
        >
          {/* Lisensi submenu */}
          <button
            onClick={() => {
              setLisensiOpen((v) => !v);
              setGenerateOpen(false);
            }}
            className={`${itemCls} justify-between`}
          >
            <span className="flex items-center gap-2.5">
              <FiKey className="h-4 w-4" />
              Lisensi
            </span>
            <FiChevronDown className={`h-4 w-4 transition-transform ${lisensiOpen ? "rotate-180" : ""}`} />
          </button>

          {lisensiOpen && (
            <div className="ml-2 border-l border-slate-100 pl-1.5 dark:border-slate-800">
              <button
                onClick={() => setGenerateOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-500/10"
              >
                <span className="flex items-center gap-2.5">
                  <FiMail className="h-4 w-4" />
                  Generate
                </span>
                <FiChevronDown className={`h-3.5 w-3.5 transition-transform ${generateOpen ? "rotate-180" : ""}`} />
              </button>
              {generateOpen && (
                <div className="py-1">
                  {durasiOptions.map((durasi) => (
                    <button
                      key={durasi.days}
                      onClick={() => {
                        closeAll();
                        onGenerate(toko, durasi.days);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-left text-xs font-medium text-slate-600 transition-colors hover:bg-primary-50 hover:text-primary-600 dark:text-slate-400 dark:hover:bg-primary-500/10 dark:hover:text-primary-400"
                    >
                      <FiKey className="h-3.5 w-3.5" />
                      {durasi.label}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={run(onKirimTelegram)} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10">
                <FaTelegram className="h-4 w-4" />
                Kirim ke Telegram
              </button>
              <button onClick={run(onDetail)} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
                <FiList className="h-4 w-4" />
                Detail
              </button>
            </div>
          )}

          <div className="my-1.5 h-px bg-slate-100 dark:bg-slate-800" />

          <button onClick={run(onKirimZip)} className={itemCls}>
            <FiPackage className="h-4 w-4" />
            Kirim ZIP (App)
          </button>
          <button onClick={run(onKirimTutorial)} className={itemCls}>
            <FiVideo className="h-4 w-4" />
            Kirim Tutorial
          </button>
          <button onClick={run(onEdit)} className={itemCls}>
            <FiEdit className="h-4 w-4" />
            Edit Email
          </button>
          <button onClick={run(onEditUserId)} className={itemCls}>
            <FiUsers className="h-4 w-4" />
            Edit User ID
          </button>

          <div className="my-1.5 h-px bg-slate-100 dark:bg-slate-800" />
          <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Ubah Jenis
          </p>
          <div className="flex gap-1.5 px-3 pb-1.5">
            {["free", "old", "new"].map((jenis) => (
              <button
                key={jenis}
                onClick={() => {
                  closeAll();
                  onEditJenis(toko, jenis);
                }}
                className={`flex-1 rounded-lg px-2 py-1 text-xs font-semibold capitalize transition-colors ${
                  toko.jenis === jenis
                    ? "bg-primary-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                {jenis}
              </button>
            ))}
          </div>

          <div className="my-1.5 h-px bg-slate-100 dark:bg-slate-800" />
          <button
            onClick={run(onDelete)}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-danger-600 transition-colors hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10"
          >
            <FiTrash2 className="h-4 w-4" />
            Hapus
          </button>
        </div>
      )}
    </div>
  );
}

const jenisTone = (jenis) => (jenis === "new" ? "success" : jenis === "old" ? "warning" : "slate");

export default function StoresPanel() {
  const confirm = useConfirm();
  const prompt = usePrompt();

  const [tokoList, setTokoList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatedLicense, setGeneratedLicense] = useState(null);
  const [lisensiDetail, setLisensiDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const durasiOptions = [
    { days: 3, label: "3 Hari" },
    { days: 7, label: "7 Hari" },
    { days: 30, label: "30 Hari" },
    { days: 90, label: "90 Hari" },
    { days: 180, label: "180 Hari" },
    { days: 365, label: "365 Hari" },
  ];

  useEffect(() => {
    fetchToko();

    const tokoSubscription = supabase
      .channel("public:toko")
      .on("postgres_changes", { event: "*", schema: "public", table: "toko" }, () => fetchToko())
      .subscribe();

    return () => supabase.removeChannel(tokoSubscription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchToko = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("toko").select("*").order("created_at", { ascending: false });
    if (error) {
      setError(error.message);
      setTokoList([]);
    } else {
      setTokoList(data);
      setFilteredList(data);
      setError(null);
    }
    setLoading(false);
  };

  const handleDelete = async (toko) => {
    const ok = await confirm({
      title: "Hapus Toko",
      message: `Yakin ingin menghapus toko "${toko.kode_toko}"? Tindakan ini tidak dapat dibatalkan.`,
      confirmLabel: "Ya, Hapus",
      tone: "danger",
    });
    if (!ok) return;

    const { error } = await supabase.from("toko").delete().eq("user_id", toko.user_id);
    if (error) {
      toast.error("Gagal hapus toko: " + error.message);
    } else {
      toast.success("Toko berhasil dihapus");
    }
  };

  // Generate lisensi lokal (hanya tampilkan, tidak kirim)
  const handleGenerateLisensi = async (toko, expiryDays = 30) => {
    try {
      const res = await fetch(`${API_BASE_URL}/generate-license-only`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kode_toko: toko.kode_toko,
          expiry_days: expiryDays,
        }),
      });

      const result = await res.json();
      if (res.ok) {
        setGeneratedLicense({
          kode_toko: toko.kode_toko,
          expiry_days: expiryDays,
          license_data: result.license_data,
          qr1_base64: result.qr1_base64,
          qr2_base64: result.qr2_base64,
        });
        toast.success(`✅ Lisensi ${expiryDays} hari berhasil di-generate`);
      } else {
        toast.error("❌ Gagal generate lisensi: " + result.error);
      }
    } catch (err) {
      toast.error("Terjadi kesalahan: " + err.message);
    }
  };

  // Kirim lisensi ke Telegram
  const handleKirimTelegram = async (toko) => {
    const ok = await confirm({
      title: "Kirim Lisensi",
      message: `Kirim lisensi ke Telegram untuk ${toko.kode_toko}?`,
      confirmLabel: "Kirim",
    });
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE_URL}/kirim-lisensi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: toko.user_id,
          kode_toko: toko.kode_toko,
          email: toko.email,
        }),
      });

      const result = await res.json();
      if (res.ok) {
        toast.success("✅ Lisensi berhasil dikirim ke Telegram");
      } else {
        toast.error("❌ Gagal kirim lisensi: " + result.error);
      }
    } catch (err) {
      toast.error("Terjadi kesalahan: " + err.message);
    }
  };

  // Lihat detail history lisensi
  const handleLisensiDetail = async (toko) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_BASE_URL}/lisensi-detail/${toko.kode_toko}`);
      const result = await res.json();

      if (res.ok) {
        setLisensiDetail({
          kode_toko: toko.kode_toko,
          email: toko.email,
          total_generated: result.total_generated,
          lisensi_list: result.lisensi_list,
        });
      } else {
        toast.error("❌ Gagal mengambil detail: " + result.error);
      }
    } catch (err) {
      toast.error("Terjadi kesalahan: " + err.message);
    }
    setLoadingDetail(false);
  };

  const handleKirimZip = async (toko) => {
    const ok = await confirm({
      title: "Kirim Aplikasi",
      message: `Kirim Aplikasi (ZIP) ke email ${toko.email}?`,
      confirmLabel: "Kirim",
    });
    if (!ok) return;

    const toastId = toast.loading("Sedang memproses ZIP dan mengirim email...");

    try {
      const res = await fetch(`${API_BASE_URL}/send-zip-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kode_toko: toko.kode_toko }),
      });

      const result = await res.json();
      if (res.ok) {
        toast.update(toastId, { render: "✅ " + result.message, type: "success", isLoading: false, autoClose: 3000 });
      } else {
        toast.update(toastId, { render: "❌ Gagal: " + result.error, type: "error", isLoading: false, autoClose: 3000 });
      }
    } catch (err) {
      toast.update(toastId, { render: "❌ Error: " + err.message, type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const handleKirimTutorial = async (toko) => {
    const ok = await confirm({
      title: "Kirim Tutorial",
      message: `Kirim link tutorial ke Telegram untuk ${toko.kode_toko}?`,
      confirmLabel: "Kirim",
    });
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE_URL}/send-tutorial`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kode_toko: toko.kode_toko }),
      });

      const result = await res.json();
      if (res.ok) {
        toast.success("✅ " + result.message);
      } else {
        toast.error("❌ " + result.error);
      }
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const handleEdit = async (toko) => {
    const newEmail = await prompt({
      title: "Ubah Email Toko",
      label: "Email",
      defaultValue: toko.email,
      placeholder: "nama@email.com",
    });
    if (newEmail === null || newEmail === toko.email) return;
    if (!newEmail.trim()) {
      toast.error("Email tidak boleh kosong");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/edit-toko`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kode_toko: toko.kode_toko, new_email: newEmail }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success("✅ " + result.message);
        fetchToko();
      } else {
        toast.error("❌ " + result.error);
      }
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const handleEditUserId = async (toko) => {
    const newUserId = await prompt({
      title: "Ubah User ID Telegram",
      label: "User ID",
      defaultValue: toko.user_id,
    });
    if (newUserId === null || newUserId === toko.user_id) return;
    if (!newUserId.trim()) {
      toast.error("User ID tidak boleh kosong");
      return;
    }

    try {
      const { error } = await supabase
        .from("toko")
        .update({ user_id: newUserId.trim() })
        .eq("user_id", toko.user_id);

      if (error) throw error;

      toast.success(`✅ User ID berhasil diubah ke "${newUserId.trim()}"`);
      fetchToko();
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const handleEditJenis = async (toko, newJenis) => {
    try {
      const { error } = await supabase.from("toko").update({ jenis: newJenis }).eq("kode_toko", toko.kode_toko);
      if (error) throw error;
      toast.success(`✅ Jenis toko diubah ke "${newJenis}"`);
      fetchToko();
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const handleSearch = (e) => {
    const keyword = e.target.value.toLowerCase();
    setSearchKeyword(keyword);
    const filtered = tokoList.filter(
      (t) =>
        t.kode_toko.toLowerCase().includes(keyword) ||
        t.email.toLowerCase().includes(keyword) ||
        t.user_id.toLowerCase().includes(keyword)
    );
    setFilteredList(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredList.slice(startIndex, startIndex + itemsPerPage);

  const counts = {
    total: tokoList.length,
    free: tokoList.filter((t) => t.jenis === "free").length,
    old: tokoList.filter((t) => t.jenis === "old").length,
    new: tokoList.filter((t) => t.jenis === "new").length,
  };

  const columns = [
    {
      key: "kode_toko",
      header: "Kode Toko",
      cell: (toko) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
            <FiShoppingBag className="h-4 w-4" />
          </span>
          <span className="font-semibold text-slate-900 dark:text-slate-100">{toko.kode_toko}</span>
        </div>
      ),
    },
    {
      key: "jenis",
      header: "Jenis",
      cell: (toko) => <Badge tone={jenisTone(toko.jenis)}>{toko.jenis || "-"}</Badge>,
    },
    {
      key: "email",
      header: "Email",
      hideBelow: "lg",
      cell: (toko) => <span className="text-slate-600 dark:text-slate-400">{toko.email}</span>,
    },
    {
      key: "user_id",
      header: "User ID",
      hideBelow: "xl",
      cell: (toko) => <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{toko.user_id}</span>,
    },
    {
      key: "referal",
      header: "Referal",
      hideBelow: "xl",
      cell: (toko) => <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{toko.kode_referal || "-"}</span>,
    },
    {
      key: "created_at",
      header: "Tanggal Daftar",
      hideBelow: "lg",
      cell: (toko) => <span className="text-sm text-slate-500 dark:text-slate-400">{formatDate(toko.created_at)}</span>,
    },
    {
      key: "aksi",
      header: "Aksi",
      headerClassName: "text-right",
      className: "text-right",
      cell: (toko) => (
        <div className="flex justify-end">
          <StoreRowActions
            toko={toko}
            durasiOptions={durasiOptions}
            onGenerate={handleGenerateLisensi}
            onKirimTelegram={handleKirimTelegram}
            onDetail={handleLisensiDetail}
            onKirimZip={handleKirimZip}
            onKirimTutorial={handleKirimTutorial}
            onEdit={handleEdit}
            onEditUserId={handleEditUserId}
            onEditJenis={handleEditJenis}
            onDelete={handleDelete}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Manajemen Toko"
        subtitle="Kelola seluruh toko yang terdaftar"
        icon={FiShoppingBag}
        actions={
          <>
            <Tooltip label="Muat ulang">
              <Button variant="secondary" size="icon" onClick={fetchToko} aria-label="Muat ulang">
                <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </Tooltip>
            <ExportButton data={filteredList} filename="toko" />
          </>
        }
      />

      {/* Mini stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Total Toko", value: counts.total, tone: "text-slate-900 dark:text-white" },
          { label: "Free", value: counts.free, tone: "text-slate-600 dark:text-slate-300" },
          { label: "Old", value: counts.old, tone: "text-warning-600 dark:text-warning-400" },
          { label: "New", value: counts.new, tone: "text-success-600 dark:text-success-400" },
        ].map((s) => (
          <Card key={s.label} className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{s.label}</p>
            <p className={`mt-1 text-2xl font-bold ${s.tone}`}>{s.value}</p>
          </Card>
        ))}
      </div>

      <Card>
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari kode toko, email, atau user ID..."
              value={searchKeyword}
              onChange={handleSearch}
              className="field-base pl-10"
            />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-200">{filteredList.length}</span> toko
          </p>
        </div>

        {loading ? (
          <TableSkeleton rows={10} columns={4} />
        ) : error ? (
          <div className="m-4 rounded-xl border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-300">
            {error}
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={currentItems}
            rowKey={(toko) => toko.user_id}
            empty={
              <EmptyState
                icon={FiShoppingBag}
                title={searchKeyword ? "Toko tidak ditemukan" : "Belum ada toko"}
                description={searchKeyword ? "Coba kata kunci lain." : "Toko yang mendaftar akan tampil di sini."}
              />
            }
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-100 px-4 py-3.5 dark:border-slate-800 sm:flex-row">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Menampilkan {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredList.length)} dari {filteredList.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                aria-label="Halaman sebelumnya"
              >
                <FiChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                aria-label="Halaman berikutnya"
              >
                <FiChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Modal Lisensi Generated */}
      <Modal
        open={!!generatedLicense}
        onClose={() => setGeneratedLicense(null)}
        title="Lisensi Generated"
        subtitle={generatedLicense ? `Kode Toko: ${generatedLicense.kode_toko}` : ""}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setGeneratedLicense(null)}>
            Tutup
          </Button>
        }
      >
        {generatedLicense && (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200/80 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
              <h4 className="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-200">License Data</h4>
              <dl className="space-y-2 text-sm">
                {[
                  ["Store Code", generatedLicense.license_data?.storeCode],
                  ["Secret Key", `${generatedLicense.license_data?.secretKey?.substring(0, 20)}...`],
                  ["Expiry Duration", generatedLicense.license_data?.expiryDuration],
                  ["Timestamp", generatedLicense.license_data?.timestamp],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-4">
                    <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                    <dd className="truncate font-mono text-xs text-slate-800 dark:text-slate-200">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-200/80 bg-white p-4 text-center dark:border-slate-800 dark:bg-slate-900">
                <h4 className="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-200">QR1 - Full License</h4>
                <img
                  src={`data:image/png;base64,${generatedLicense.qr1_base64}`}
                  alt="QR Code 1"
                  className="mx-auto h-44 w-44 object-contain"
                />
              </div>
              <div className="rounded-xl border border-slate-200/80 bg-white p-4 text-center dark:border-slate-800 dark:bg-slate-900">
                <h4 className="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-200">QR2 - Quick Verify</h4>
                <img
                  src={`data:image/png;base64,${generatedLicense.qr2_base64}`}
                  alt="QR Code 2"
                  className="mx-auto h-44 w-44 object-contain"
                />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200/80 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
              <h4 className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">License Code</h4>
              <p className="break-all rounded-lg bg-slate-100 p-3 font-mono text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-300">
                {generatedLicense.license_data?.licenseCode}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Detail Lisensi */}
      <Modal
        open={!!lisensiDetail}
        onClose={() => setLisensiDetail(null)}
        title="Detail Lisensi"
        subtitle={lisensiDetail ? `Kode Toko: ${lisensiDetail.kode_toko}` : ""}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setLisensiDetail(null)}>
            Tutup
          </Button>
        }
      >
        {lisensiDetail && (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-primary-100 bg-primary-50 p-4 dark:border-primary-500/20 dark:bg-primary-500/10">
              <div>
                <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Total Lisensi Di-generate</p>
                <p className="text-2xl font-bold text-primary-800 dark:text-primary-200">{lisensiDetail.total_generated}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{lisensiDetail.email}</p>
              </div>
            </div>

            {lisensiDetail.lisensi_list.length === 0 ? (
              <EmptyState icon={FiList} title="Belum ada lisensi" description="Lisensi yang di-generate akan tampil di sini." />
            ) : (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Riwayat Generate Lisensi</h4>
                {lisensiDetail.lisensi_list.map((lisensi, idx) => (
                  <div
                    key={lisensi.id || idx}
                    className="rounded-xl border border-slate-200/80 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40"
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <Badge tone="primary">#{lisensiDetail.total_generated - idx}</Badge>
                      <Badge tone="success">{lisensi.expiry_duration} Hari</Badge>
                    </div>
                    <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                      <p className="text-slate-500 dark:text-slate-400">
                        Timestamp: <span className="font-mono text-xs text-slate-800 dark:text-slate-200">{lisensi.timestamp}</span>
                      </p>
                      <p className="text-slate-500 dark:text-slate-400">
                        Created:{" "}
                        <span className="text-slate-800 dark:text-slate-200">
                          {lisensi.created_at ? formatDateTime(lisensi.created_at) : "-"}
                        </span>
                      </p>
                    </div>
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Hash:{" "}
                      <span className="break-all font-mono text-slate-600 dark:text-slate-300">
                        {lisensi.license_hash?.substring(0, 50)}...
                      </span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Loading overlay detail */}
      {loadingDetail && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/30 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-pop dark:border-slate-800 dark:bg-slate-900">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
            <span className="text-sm text-slate-700 dark:text-slate-200">Memuat detail lisensi...</span>
          </div>
        </div>
      )}
    </div>
  );
}
